import api from "@/lib/api";
import { useI18n } from "@/context/I18nContext";

const B = "/admin/website-production";

export const wpApi = {
  status: () => api.get(`${B}/status`),
  list: (params) => api.get(`${B}/orders`, { params }),
  get: (id) => api.get(`${B}/orders/${id}`),
  create: (body) => api.post(`${B}/orders`, body),
  update: (id, body) => api.put(`${B}/orders/${id}`, body),
  archive: (id, revision) => api.post(`${B}/orders/${id}/archive`, { revision }),
  uploadLogo: (id, file, revision) => {
    const fd = new FormData();
    fd.append("file", file);
    fd.append("revision", String(revision));
    return api.post(`${B}/orders/${id}/logo`, fd);
  },
  logoBlob: (id) => api.get(`${B}/orders/${id}/logo`, { responseType: "blob" }),
  presets: (params) => api.get(`${B}/presets`, { params }),
  createPreset: (body) => api.post(`${B}/presets`, body),
  archivePreset: (id) => api.post(`${B}/presets/${id}/archive`),
  package: (id) => api.get(`${B}/orders/${id}/package`),
  packageQueue: () => api.get(`${B}/package-queue`),
  enqueuePackages: (orders) => api.post(`${B}/package-queue`, { orders }),
  savePackage: (id, body) => api.put(`${B}/orders/${id}/package`, body),
  uploadPackagePreview: (id, file, slot, revision) => {
    const fd = new FormData(); fd.append("file", file); fd.append("slot", String(slot)); fd.append("package_revision", String(revision));
    return api.post(`${B}/orders/${id}/package/previews`, fd);
  },
  approvePackageWeb01: (id, revision) => api.post(`${B}/orders/${id}/package/approve-web01`, { package_revision: revision }),
  generatePackage: (id, revision) => api.post(`${B}/orders/${id}/package/generate`, { package_revision: revision }),
  reviewPackageDocuments: (id, revision, manifest) => api.post(`${B}/orders/${id}/package/review-documents`, { package_revision: revision, manifest_sha256: manifest }),
  packageAsset: (id, aid) => api.get(`${B}/orders/${id}/package/assets/${aid}`, { responseType: "blob" }),
};

export const LANGS = ["de", "fr", "it", "en"];
export const LANG_NAME = { de: "Deutsch", fr: "Français", it: "Italiano", en: "English" };
const LOCALE = { de: "de-CH", fr: "fr-CH", it: "it-CH", en: "en-GB" };
export const MAX_LOGO = 2 * 1024 * 1024;
export const LOGO_EXT = ["png", "jpg", "jpeg", "webp", "svg"];
export const FIELD_LABEL = {
  company: "Firmenname", website_url: "WWW-Link", goal: "Ziel", languages: "Sprachen",
  page_scope: "Seitenumfang", notes: "Notizen", logo: "Logo", name: "Name der Vorlage", preset_id: "Vorlage", revision: "Version",
};
export const STATUS = {
  entwurf: { label: "Entwurf", cls: "bg-amber-400/15 text-amber-300 border-amber-400/30" },
  vollstaendig: { label: "Auftragsdaten vollständig", cls: "bg-[#A3E635]/15 text-[#A3E635] border-[#A3E635]/30" },
  archiviert: { label: "Archiviert", cls: "bg-white/10 text-gray-400 border-white/10" },
};
// Pydantic-Fehlertypen -> deutscher Quelltext (wird ueber das Sprachsystem uebersetzt)
const PYD = {
  missing: "Pflichtangabe fehlt", string_too_short: "Text ist zu kurz", string_too_long: "Text ist zu lang",
  too_short: "Zu wenige Einträge", too_long: "Zu viele Einträge", less_than_equal: "Wert ist zu gross",
  greater_than_equal: "Wert ist zu klein", int_type: "Bitte eine ganze Zahl angeben", int_parsing: "Bitte eine ganze Zahl angeben",
  extra_forbidden: "Unbekanntes Feld", string_pattern_mismatch: "Ungültiges Format", string_type: "Bitte einen Text angeben",
  list_type: "Bitte eine Liste angeben",
};

export const inputCls = "w-full bg-black border border-white/10 rounded-lg px-3 py-2.5 text-sm text-white placeholder-gray-600 focus:outline-none focus:border-[#A3E635] transition-colors";
export const btnPrimary = "inline-flex items-center justify-center gap-2 bg-[#A3E635] text-black font-bold rounded-lg px-4 py-2.5 text-sm hover:bg-[#B4F04A] active:scale-[0.98] transition-[background-color,transform] disabled:opacity-50 disabled:pointer-events-none";
export const btnGhost = "inline-flex items-center justify-center gap-2 border border-white/10 bg-white/[0.03] text-gray-200 rounded-lg px-4 py-2.5 text-sm hover:bg-white/[0.08] transition-colors disabled:opacity-50";
export const focusRing = "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#A3E635] focus-visible:ring-offset-2 focus-visible:ring-offset-black";

export const langText = (langs) => (langs || []).map((l) => l.toUpperCase()).join(", ") || "–";
export const newKey = () => (window.crypto?.randomUUID ? window.crypto.randomUUID() : `${Date.now()}${Math.random()}`).replace(/[^A-Za-z0-9_-]/g, "");

// Deutsche Servermeldung uebersetzen; Zahlen und <tags> werden als {n} gesucht und wieder eingesetzt.
const TOKEN = /\d+|<[^>]*>/g;
export const trMsg = (t, s) => {
  const exact = t(s);
  if (exact !== s) return exact;
  const vals = s.match(TOKEN);
  if (!vals) return s;
  const k = s.replace(TOKEN, "{n}");
  const out = t(k);
  if (out === k) return s;
  let i = 0;
  return out.replace(/\{n\}/g, () => vals[i++] ?? "");
};

const pydMsg = (t, x) => {
  if (x?.type === "value_error") return trMsg(t, (x.msg || "").replace(/^Value error, /, ""));
  const field = [...(x?.loc || [])].reverse().find((p) => typeof p === "string" && p !== "body");
  const msg = t(PYD[x?.type] || "Ungültige Eingabe");
  return field ? `${t(FIELD_LABEL[field] || field)}: ${msg}` : msg;
};

export const errText = (t, e, fallback) => {
  const d = e?.response?.data?.detail;
  if (typeof d === "string") return trMsg(t, d);
  if (Array.isArray(d)) return d.map((x) => pydMsg(t, x)).filter(Boolean).join(" · ") || t(fallback);
  return t(fallback);
};

export function useWP() {
  const { t, lang } = useI18n();
  const locale = LOCALE[lang] || "de-CH";
  const pages = (n) => `${n} ${t(Number(n) === 1 ? "Seite" : "Seiten")}`;
  return {
    lang,
    t,
    pages,
    err: (e, fallback) => errText(t, e, fallback),
    field: (k) => t(FIELD_LABEL[k] || k),
    status: (s) => t((STATUS[s] || STATUS.entwurf).label),
    fmtDate: (iso) => (iso ? new Date(iso).toLocaleString(locale, { dateStyle: "short", timeStyle: "short" }) : "–"),
    fmtValue: (field, v) => {
      if (v === null || v === undefined || v === "") return "–";
      if (field === "languages") return langText(v);
      if (field === "page_scope") return pages(v);
      return String(v);
    },
  };
}

